import React, { useEffect, useState, useContext } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  FormHelperText,
  FormControlLabel,
  Switch,
  Box,
  Chip,
  Typography,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { trackFeature } from '../utils/analytics';
import EmojiDropdown from '../utils/EmojiDropdown';
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  getDocs,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { AppContext } from '../AppContext';
import { useTaskGenerator } from '../utils/useTaskGenerator';
import { SnackbarContext } from '../SnackbarContext';

const hslToHex = (h, s, l) => {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

const getRandomCustomTaskColor = () => {
  const hue = Math.floor(Math.random() * 360);
  const saturation = Math.floor(Math.random() * 31) + 55;
  const lightness = Math.floor(Math.random() * 16) + 45;
  return hslToHex(hue, saturation, lightness);
};

const LIGHTS_TASK_COLOR = '#d37e3d';

function AddTask({ open, onClose, onAdd, editMode = false, taskToEdit = {} }) {
  const { user, userData, farmData } = useContext(AppContext);
    const activeFarmId = userData?.activeFarmId;
    const { showSuccess, showError } = useContext(SnackbarContext);
  const defaultDateFormat = farmData?.settings?.dateFormat || 'dd-MM-yyyy';
  const taskTypeDefaults = {
    sow: { emoji: '🌱', color: 'success' },
    lights: { emoji: '💡', color: LIGHTS_TASK_COLOR },
    water: { emoji: '💧', color: 'primary' },
    harvest: { emoji: '✂️', color: 'error' },
    delivery: { emoji: '🚚', color: 'tertiary' },
  };

  const [newTask, setNewTask] = useState({
    name: '',
    label: '',
    date: null,
    completed: false,
    type: '',
    emoji: '',
    color: null,
    recurring: false,
    recurringUntil: null,
  });
  const [touched, setTouched] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const generateRecurringTasks = useTaskGenerator();

  useEffect(() => {
    if (!open) return;
    if (editMode && taskToEdit && taskToEdit.id) {
      setNewTask({
        ...taskToEdit,
        date: taskToEdit.date ? new Date(taskToEdit.date) : null,
        recurring: taskToEdit.recurring ?? false,
        recurringUntil: taskToEdit.recurringUntil
          ? new Date(taskToEdit.recurringUntil)
          : null,
        color: taskToEdit.color || null,
      });
    } else {
      setNewTask({
        name: '',
        label: '',
        date: null,
        completed: false,
        type: '',
        emoji: '',
        color: null,
        recurring: false,
        recurringUntil: null,
      });
    }
    setTouched({});
    setFormSubmitted(false);
  }, [open, editMode, taskToEdit]);

  const handleBlur = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));
  const isError = (field) =>
    (!newTask[field] || (field === 'recurringUntil' && newTask.recurring)) &&
    (touched[field] || formSubmitted);

  const handleCloseDialog = () => {
    setFormSubmitted(false);
    setTouched({});
    onClose();
  };

  const typeDefaults = taskTypeDefaults[newTask.type] || {};
  const previewEmoji =
    newTask.type === 'custom' ? newTask.emoji : typeDefaults.emoji || newTask.emoji;
  const previewColor =
    newTask.type === 'custom' ? newTask.color : typeDefaults.color || newTask.color;
  const previewLabel = `${previewEmoji || ''} ${newTask.name || 'New task'}`.trim();
  const isCustomPreview = newTask.type === 'custom';
  const isHexPreview = typeof previewColor === 'string' && previewColor.startsWith('#');

  const handleSave = async () => {
  setFormSubmitted(true);

  // 1) Validation
  if (
    !newTask.name ||
    !newTask.label ||
    !newTask.date ||
    !newTask.type ||
    (newTask.recurring && !newTask.recurringUntil)
  ) {
    return;
  }

  try {
    if (!user?.uid) throw new Error('User not logged in');
    if (!activeFarmId) throw new Error('No active farm selected');

    const tasksCol = collection(db, 'farms', activeFarmId, 'tasks');

    // 2) Build payload
    const payload = {
      name: newTask.name,
      label: newTask.label,
      date: newTask.date.toISOString(),
      completed: newTask.completed || false,
      type: newTask.type,
      emoji: newTask.emoji,
      color: newTask.color,
      recurring: newTask.recurring,
      recurringUntil: newTask.recurringUntil
        ? newTask.recurringUntil.toISOString()
        : null,
      originalRecurringTaskId: editMode
        ? taskToEdit.originalRecurringTaskId || taskToEdit.id
        : null,
    };

    // 3) Create or update master task
    let primaryId;
    if (editMode && taskToEdit.id) {
      await updateDoc(doc(tasksCol, taskToEdit.id), payload);
      primaryId = taskToEdit.id;

      // 4) Remove downstream if editing
      const originId =
        taskToEdit.originalRecurringTaskId || taskToEdit.id;
      const childrenQ = query(
        tasksCol,
        where('originalRecurringTaskId', '==', originId)
      );
      const snap = await getDocs(childrenQ);
      for (let d of snap.docs) {
        const recDate = new Date(d.data().date);
        if (recDate >= new Date(payload.date)) {
          await deleteDoc(doc(tasksCol, d.id));
        }
      }
    } else {
      const ref = await addDoc(tasksCol, payload);
      primaryId = ref.id;
    }

    // 5) Rebuild occurrences if recurring
    if (newTask.recurring && newTask.recurringUntil) {
      const occurrences = generateRecurringTasks({
        ...payload,
        id: primaryId,
      });
      // skip the first one (master)
      const batch = occurrences.slice(1).map((occ) =>
        addDoc(tasksCol, occ)
      );
      await Promise.all(batch);
    }

    // 6) Analytics + snackbar
    if (editMode) {
      trackFeature('task_updated', { id: primaryId, label: payload.label });
      showSuccess(`Task “${payload.label}” updated.`);
    } else {
      trackFeature('task_created', { id: primaryId, label: payload.label });
      showSuccess(`Task “${payload.label}” added.`);
    }

    // 7) Cleanup
    handleCloseDialog();
    onAdd?.({ ...payload, id: primaryId });
  } catch (err) {
    console.error('Error saving task:', err);
    showError('Error saving task.');
  }
};

  const handleDialogKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCloseDialog();
      return;
    }

    if (e.key !== 'Enter') return;
    if (e.defaultPrevented || e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;

    const target = e.target;
    const tag = target?.tagName?.toLowerCase();
    if (tag === 'textarea' || target?.isContentEditable) return;
    if (target?.closest?.('[role="listbox"], [role="menu"]')) return;

    e.preventDefault();
    handleSave();
  };


  return (
    <Dialog open={open} onClose={handleCloseDialog} onKeyDown={handleDialogKeyDown} fullWidth>
      <DialogTitle>{editMode ? 'Edit Task' : 'Add New Task'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Task Name */}
          <Grid item xs={5}>
            <TextField
              label="Task Name"
              fullWidth
              required
              value={newTask.name}
              onChange={(e) =>
                setNewTask((prev) => ({ ...prev, name: e.target.value }))
              }
              onBlur={() => handleBlur('name')}
              error={isError('name')}
              helperText={isError('name') && 'Required'}
            />
          </Grid>

          {/* Task Description */}
          <Grid item xs={7}>
            <TextField
              label="Task Description"
              fullWidth
              required
              value={newTask.label}
              onChange={(e) =>
                setNewTask((prev) => ({ ...prev, label: e.target.value }))
              }
              onBlur={() => handleBlur('label')}
              error={isError('label')}
              helperText={isError('label') && 'Required'}
            />
          </Grid>

          {/* Due Date */}
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Due Date"
                value={newTask.date}
                onChange={(val) =>
                  setNewTask((prev) => ({ ...prev, date: val }))
                }
                inputFormat={defaultDateFormat}
                disableMaskedInput
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    required
                    onBlur={() => handleBlur('date')}
                    error={isError('date')}
                    helperText={isError('date') && 'Required'}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>

          {/* Task Type */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth required error={isError('type')}>
              <InputLabel>Task Type</InputLabel>
              <Select
                value={newTask.type}
                label="Task Type"
                onChange={(e) =>
                  setNewTask((prev) => ({
                    ...prev,
                    type: e.target.value,
                    color:
                      e.target.value === 'custom' ? getRandomCustomTaskColor() : null,
                    emoji:
                      e.target.value === 'custom' ? prev.emoji : '',
                  }))
                }
                onBlur={() => handleBlur('type')}
              >
                <MenuItem value="sow">Sow</MenuItem>
                <MenuItem value="lights">Lights</MenuItem>
                <MenuItem value="water">Water</MenuItem>
                <MenuItem value="harvest">Harvest</MenuItem>
                <MenuItem value="delivery">Delivery</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </Select>
              <FormHelperText>
                {isError('type') && 'Required'}
              </FormHelperText>
            </FormControl>
          </Grid>

          {/* Recurring */}
          <Grid item xs={12} sm={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', height: 40, mt: 1, mb: 0.5 }}>
              <FormControlLabel
                label="Recurring"
                labelPlacement="start"
                sx={{ mr: 0, ml: 0 }}
                control={
                  <Switch
                    checked={Boolean(newTask.recurring)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setNewTask((prev) => ({
                          ...prev,
                          recurring: { frequency: '', period: 'day' },
                        }));
                      } else {
                        setNewTask((prev) => ({
                          ...prev,
                          recurring: false,
                          recurringUntil: null,
                        }));
                      }
                    }}
                    inputProps={{ 'aria-label': 'Recurring task' }}
                  />
                }
              />
            </Box>
          </Grid>

          {typeof newTask.recurring === 'object' && (
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <Box sx={{ display: 'flex', width: '100%' }}>
                    <TextField
                      label="Every"
                      value={newTask.recurring.frequency}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (/^\d*$/.test(val))
                          setNewTask((prev) => ({
                            ...prev,
                            recurring: { ...prev.recurring, frequency: val },
                          }));
                      }}
                      inputProps={{ inputMode: 'numeric' }}
                      error={formSubmitted && !newTask.recurring.frequency}
                      helperText={
                        formSubmitted &&
                        !newTask.recurring.frequency &&
                        'Required'
                      }
                      sx={{
                        flex: 3,
                        '& .MuiOutlinedInput-root': {
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                          bgcolor: 'action.hover',
                        },
                      }}
                    />
                    <FormControl
                      sx={{
                        flex: 5,
                        ml: '-1px',
                        '& .MuiOutlinedInput-root': {
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <Select
                        value={newTask.recurring.period}
                        onChange={(e) =>
                          setNewTask((prev) => ({
                            ...prev,
                            recurring: {
                              ...prev.recurring,
                              period: e.target.value,
                            },
                          }))
                        }
                      >
                        <MenuItem value="day">Day(s)</MenuItem>
                        <MenuItem value="week">Week(s)</MenuItem>
                        <MenuItem value="month">Month(s)</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Until"
                      value={newTask.recurringUntil}
                      onChange={(val) =>
                        setNewTask((prev) => ({ ...prev, recurringUntil: val }))
                      }
                      inputFormat={defaultDateFormat}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={isError('recurringUntil')}
                          helperText={
                            isError('recurringUntil') && 'Required'
                          }
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </Grid>
          )}

          {/* Custom fields */}
          {newTask.type === 'custom' && (
            <>
              <Grid item xs={6}>
                <TextField
                  label="Task Color"
                  type="color"
                  fullWidth
                  value={newTask.color || '#555555'}
                  onChange={(e) =>
                    setNewTask((prev) => ({ ...prev, color: e.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <EmojiDropdown
                  label="Emoji"
                  value={newTask.emoji}
                  onChange={(emoji) =>
                    setNewTask((prev) => ({ ...prev, emoji }))
                  }
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Preview
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                minHeight: 48,
                px: 2,
                py: 1,
                borderRadius: 1,
                bgcolor: 'background.paper',
              }}
            >
              <Chip
                label={previewLabel}
                size="small"
                variant="filled"
                color={!isCustomPreview && !isHexPreview && previewColor ? previewColor : undefined}
                sx={
                  isCustomPreview || isHexPreview
                    ? {
                      backgroundColor: previewColor || '#555555',
                      color: 'white',
                      fontWeight: 600,
                    }
                    : { fontWeight: 600 }
                }
              />
            </Box>
          </Grid>

        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCloseDialog}>Cancel</Button>
        <Button variant="contained" color="secondary" onClick={handleSave}>
          {editMode ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddTask;



